import React, { useContext, useEffect } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { CButton, CForm, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import { isEmpty } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoadingButton from '../../../shared/components/LoadingButton';
import { UserInfo, UserPasswordPayload, UserPayload, UserRoles } from '../../../shared/models/user';
import { RootState } from '../../../store/state';
import { CreateUser, ResetUpdateUserState } from '../../actions/userManagement.action';
import TechnicianForm, { TechnicianFormMode } from '../../components/TechnicianForm';
import { ConfigContext } from '../../../shared/contexts/journeyConfig.context';
import phoneUnification from '../../../shared/utils/validation-utils';
import { selectUserProfile } from '../../../store/selectors/profile.selectors';
import { selectTechnicians } from '../../../store/selectors/user.selectors';
import { selectOrgHierarchySelectedOrg } from '../../../store/selectors/orgHierarchy.selectors';

const ADD_TECHNICIAN_ERROR_MESSAGES = {
  admin: 'You do not have any more licenses available. Please go to the “Account & Billing” area, located under your profile picture and name in the main menu bar, to add additional licenses for your team. You may also remove inactive technicians in order to re-assign active licenses.',
  manager: 'You do not have available licenses. Please contact administrator'
};

const validationSchema = (users: UserInfo[]) => Yup.object({
  name: Yup.string().required('Name is required'),
  phone: Yup.string()
  .nullable()
  .test(
    'phone-validation',
    'Please enter a valid US or Canada number',
    (value) => {
      if (!value) return true;
      const phone = phoneUnification(value);
      return phone && isValidPhoneNumber(phone);
    }
  ),
  roles: Yup.array().min(1, 'At least one role is required'),
  email: Yup.string().notOneOf(users.filter(u => !u.removed).map(u => u.email), 'A user with this email already exists').email('Email must be valid').required('Email is required'),
});

const initialValues: UserPayload & UserPasswordPayload = {
  name: '',
  phone: '',
  email: '',
  avatar: {},
  password: process.env.USER_DEFAULT_PASSWORD,
  roles: [UserRoles.Agent],
};

export interface AddTechnicianProps extends DispatchProp {
  show: boolean;
  close: () => void;
  saving: boolean;
  saved: boolean;
  users: UserInfo[],
  onSave?: () => void;
}

const AddTechnicianModal = (props: AddTechnicianProps) => {
  const { show, close, saving, saved, users, onSave, dispatch } = props;
  const {
    enableOrgHierarchyModule,
    enableChargebeeModule
  } = useFlags();
  const labels = useContext(ConfigContext);
  const profile = useSelector(selectUserProfile);
  const org = useSelector(selectOrgHierarchySelectedOrg);
  const agents = useSelector(selectTechnicians);
  const onSubmit = (values: UserPayload) => {
    dispatch(new CreateUser({
      orgId: org?._id,
      body: values,
      includeChildren: enableOrgHierarchyModule
    }));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema(users || []),
    onSubmit
  });

  const maxAgents = org?.billing_subscription?.max_agents;
  const isAdmin = profile?.roles.indexOf(UserRoles.Admin) > -1;
  const isManager = profile?.roles.indexOf(UserRoles.Manager) > -1 && profile?.roles.indexOf(UserRoles.Admin) === -1;
  const isTechnicianCanBeAdded = maxAgents > 0 && agents?.length - 1 < maxAgents || !enableChargebeeModule;

  const handleClose = () => {
    close();
    formik.resetForm();

    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    if (saved) {
      dispatch(new ResetUpdateUserState());
      handleClose();
    }
  }, [saved]);

  return (
    show && <CModal className="mt-5 pt-5" id="add-technician-modal" show={ show } onClose={ handleClose } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Add {labels.agent}</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            { isTechnicianCanBeAdded ?
              (
                <TechnicianForm formik={ formik } mode={TechnicianFormMode.Add} />
              ) : (
                <div>
                  {isAdmin && ADD_TECHNICIAN_ERROR_MESSAGES.admin}
                  {isManager && ADD_TECHNICIAN_ERROR_MESSAGES.manager}
                </div>
              )
            }
          </CModalBody>
          <CModalFooter className="glympse-footer">
            {
              isTechnicianCanBeAdded ? (
                <div className="glympse-btnWrapper">
                  <LoadingButton
                    color='primary'
                    className='bg-color-orange border-0 roundedBtn roundBottomShadow pl-5 pr-5'
                    type='submit'
                    isLoading={ saving }
                    disabled={!formik.dirty || saving || !isEmpty(formik.errors)}
                  >Add {labels.agent}</LoadingButton>
                </div>
              ) : null
            }
            <div className="glympse-btnWrapper">
              <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ handleClose }>Cancel</CButton>
            </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  saving: state?.userManagement?.updateUser?.saving,
  saved: state?.userManagement?.updateUser?.saved,
  users: state?.userManagement?.users?.data,
});

export default connect(mapStateToProps)(AddTechnicianModal);
